import { useState, useMemo, useEffect, type FormEvent, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import CrmLayout from "@/components/feature/CrmLayout";
import { positionColors, dataAccessBadgeColors } from "@/mocks/positionsData";
import { useToast } from "@/components/base/Toast";
import { useAuth } from "@/components/feature/AuthContext";
import OrgChart from "@/pages/settings/teams/components/OrgChart";
import type { TeamMember } from "@/pages/settings/teams/components/OrgChart";
import axios from "axios";
import branchesApi, { apiError } from "../../../components/api/Branchesapi";

const API = import.meta.env.VITE_API_URL || "https://api.nexflowcrm.com/api/v1";
const authHeader = () => ({
  Authorization: `Bearer ${localStorage.getItem("auth_token")}`,
});

// Mirrors the backend's utils/role.js exactly — must stay in sync.
const toRoleSlug = (positionName: string) =>
  positionName.trim().toLowerCase().replace(/\s+/g, "_");

const isValidEmail = (email: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

type ViewMode = "org" | "list";

type Position = {
  id: string;
  name: string;
  level: number;
  reportsTo: string | null;
  color: string;
  dataAccess: string;
  permissions: Record<string, any>;
  userCount: number;
  createdAt: string;
};

type BranchOption = { id: string; name: string; code?: string };

// Raw shape returned by /auth/company-user/all
type CompanyUser = {
  id: string;
  name: string;
  email: string;
  phone: string | null;
  role: string;
  branch: any;
  branchId: string | null;
  isActive: boolean;
  managerId: string | null;
  team: string | null;
  leadCount?: number;
  createdAt: string;
};

const getInitials = (name: string) =>
  name.trim().split(/\s+/).map((n) => n[0]).slice(0, 2).join("").toUpperCase();

export default function TeamsPage() {
  const { showToast } = useToast();
  const { user: currentUser } = useAuth();
  const navigate = useNavigate();
  const isSuperAdmin = currentUser?.role === "super_admin" || currentUser?.role === "admin";

  const [viewMode, setViewMode] = useState<ViewMode>("org");
  const [companyUsers, setCompanyUsers] = useState<CompanyUser[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [detailPopup, setDetailPopup] = useState<TeamMember | null>(null);

  // Edit member modal state
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingUserId, setEditingUserId] = useState<string | null>(null);
  const [editName, setEditName] = useState("");
  const [editEmail, setEditEmail] = useState("");
  const [editPhone, setEditPhone] = useState("");
  const [editPassword, setEditPassword] = useState("");
  const [editPositionId, setEditPositionId] = useState("");
  const [editManagerId, setEditManagerId] = useState("");
  const [editBranchId, setEditBranchId] = useState("");
  const [editTeamName, setEditTeamName] = useState("");
  const [editFormError, setEditFormError] = useState("");

  // Reassign modal state
  const [reassignModalOpen, setReassignModalOpen] = useState(false);
  const [reassignMember, setReassignMember] = useState<TeamMember | null>(null);
  const [reassignTargetAgent, setReassignTargetAgent] = useState("");
  const [reassignTransferReports, setReassignTransferReports] = useState(true);
  const [isReassigning, setIsReassigning] = useState(false);

  // Add member form state
  const [newName, setNewName] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [newPhone, setNewPhone] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [newPositionId, setNewPositionId] = useState("");
  const [newManagerId, setNewManagerId] = useState("");
  const [newBranchId, setNewBranchId] = useState("");
  const [sendCredentials, setSendCredentials] = useState(true);
  const [formError, setFormError] = useState("");

  const [positionsList, setPositionsList] = useState<Position[]>([]);
  const [branchList, setBranchList] = useState<BranchOption[]>([]);

  // ─── Fetch positions ─────────────────────────────────────
  const fetchPositions = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/settings/positions`, { headers: authHeader() });
      setPositionsList(res.data?.data?.items || []);
    } catch (err: any) {
      console.error(err.response?.data?.message || "Failed to load positions");
    }
  }, []);

  useEffect(() => {
    fetchPositions();
  }, [fetchPositions]);

  // ─── Fetch branches ──────────────────────────────────────
  const fetchBranches = useCallback(async () => {
    try {
      const { items } = await branchesApi.list();
      setBranchList(items);
    } catch (err) {
      console.error(apiError(err, "Couldn't load branches"));
    }
  }, []);

  useEffect(() => {
    fetchBranches();
  }, [fetchBranches]);

  // ─── Fetch real company users ────────────────────────────
  const fetchCompanyUsers = useCallback(async () => {
    setLoadingUsers(true);
    try {
      const res = await axios.get(`${API}/auth/company-user/all`, { headers: authHeader() });
      const items = res.data?.data?.items || res.data?.items || (Array.isArray(res.data) ? res.data : []);
      setCompanyUsers(items);
    } catch (err: any) {
      showToast(err.response?.data?.message || "Failed to load team members", "error");
    } finally {
      setLoadingUsers(false);
    }
  }, [showToast]);

  useEffect(() => {
    fetchCompanyUsers();
  }, [fetchCompanyUsers]);

  // Map real users into the view-model the table/OrgChart expect
  const teamMembers: TeamMember[] = useMemo(
    () =>
      companyUsers.map((u) => {
        const pos = positionsList.find((p) => toRoleSlug(p.name) === u.role);
        return {
          id: u.id,
          name: u.name,
          email: u.email,
          phone: u.phone || "—",
          positionId: pos?.id || "",
          teamName: u.team || "—",
          branch: u.branch || "—",
          avatarInitials: getInitials(u.name),
          leadCount: u.leadCount || 0,
          reportsToMemberId: u.managerId || "",
        };
      }),
    [companyUsers, positionsList]
  );

  // Computed state for ADD member
  const selectedPosition = useMemo(
    () => positionsList.find((p) => p.id === newPositionId) || null,
    [newPositionId, positionsList]
  );

  const parentPosition = useMemo(() => {
    if (!selectedPosition?.reportsTo) return null;
    return positionsList.find((p) => p.name === selectedPosition.reportsTo) || null;
  }, [selectedPosition, positionsList]);

  const eligibleManagers = useMemo(() => {
    if (!parentPosition) return [];
    const parentSlug = toRoleSlug(parentPosition.name);
    return companyUsers.filter((u) => u.isActive && u.role === parentSlug);
  }, [parentPosition, companyUsers]);

  const autoSelectedManager = useMemo(
    () => (eligibleManagers.length === 1 ? eligibleManagers[0] : null),
    [eligibleManagers]
  );

  const effectiveManagerId = autoSelectedManager ? autoSelectedManager.id : newManagerId;

  const autoTeamName = useMemo(() => {
    if (!selectedPosition || selectedPosition.level === 1) return "";
    const mgr = companyUsers.find((u) => u.id === effectiveManagerId);
    if (mgr?.team) return mgr.team;
    if (!parentPosition?.reportsTo) return "Leadership";
    const branchName = typeof mgr?.branch === "object" && mgr?.branch !== null ? mgr.branch.name : mgr?.branch;
    if (branchName) return `${branchName} Team`;
    return `${selectedPosition.name} Team`;
  }, [selectedPosition, parentPosition, companyUsers, effectiveManagerId]);

  // Computed state for EDIT member
  const editSelectedPosition = useMemo(
    () => positionsList.find((p) => p.id === editPositionId) || null,
    [editPositionId, positionsList]
  );

  const editEligibleManagers = useMemo(() => {
    return companyUsers.filter((u) => u.id !== editingUserId && u.isActive);
  }, [companyUsers, editingUserId]);

  const stats = useMemo(() => {
    const totalMembers = teamMembers.length;
    const uniquePositions = new Set(teamMembers.map((m) => m.positionId).filter(Boolean)).size;
    const uniqueTeams = new Set(teamMembers.map((m) => m.teamName).filter((t) => t !== "—")).size;
    const avgLeads = totalMembers > 0
      ? Math.round(teamMembers.reduce((sum, m) => sum + m.leadCount, 0) / totalMembers)
      : 0;
    return { totalMembers, positions: uniquePositions, teams: uniqueTeams, avgLeads };
  }, [teamMembers]);

  const canSubmit = useMemo(() => {
    if (!selectedPosition) return false;
    if (selectedPosition.level === 1) return true;
    return !!effectiveManagerId;
  }, [selectedPosition, effectiveManagerId]);

  const resetForm = () => {
    setNewName("");
    setNewEmail("");
    setNewPhone("");
    setNewPassword("");
    setNewPositionId("");
    setNewManagerId("");
    setNewBranchId("");
    setSendCredentials(true);
    setFormError("");
  };

  const handleAddMember = async (e: FormEvent) => {
    e.preventDefault();
    setFormError("");

    if (!newName.trim()) return setFormError("Name is required");
    if (!newEmail.trim() || !isValidEmail(newEmail.trim())) return setFormError("A valid email is required");
    if (!newPassword.trim() || newPassword.length < 5) return setFormError("Password must be at least 5 characters");
    if (!selectedPosition) return setFormError("Position is required");
    if (selectedPosition.level > 1 && !effectiveManagerId) return setFormError("A direct manager is required for this position");

    const branch = branchList.find((b) => b.id === newBranchId);

    setIsSaving(true);
    try {
      await axios.post(
        `${API}/auth/company-user/create`,
        {
          name: newName.trim(),
          email: newEmail.trim(),
          phone: newPhone.trim() || null,
          password: newPassword,
          role: toRoleSlug(selectedPosition.name),
          branch: branch?.name || null,
          branchId: newBranchId || null,
          status: "Active",
          managerId: effectiveManagerId || null,
          team: autoTeamName || null,
        },
        { headers: authHeader() }
      );

      showToast("Team member added successfully", "success");
      setAddModalOpen(false);
      resetForm();
      await fetchCompanyUsers();
    } catch (err: any) {
      const message = err.response?.data?.message || "Failed to add team member";
      setFormError(message);
      showToast(message, "error");
    } finally {
      setIsSaving(false);
    }
  };

  // Open edit member modal prefilled
  const handleEditMember = (member: TeamMember) => {
    const rawUser = companyUsers.find((u) => u.id === member.id);
    if (!rawUser) return;

    const currentPos = positionsList.find((p) => toRoleSlug(p.name) === rawUser.role);
    const branchName = typeof rawUser.branch === "object" && rawUser.branch !== null ? rawUser.branch.name : rawUser.branch;
    const currentBranch = branchList.find((b) => b.id === rawUser.branchId || b.name === branchName);

    setEditingUserId(rawUser.id);
    setEditName(rawUser.name || "");
    setEditEmail(rawUser.email || "");
    setEditPhone(rawUser.phone || "");
    setEditPassword("");
    setEditPositionId(currentPos?.id || "");
    setEditManagerId(rawUser.managerId || "");
    setEditBranchId(currentBranch?.id || rawUser.branchId || "");
    setEditTeamName(rawUser.team || "");
    setEditFormError("");
    setEditModalOpen(true);
  };

  const handleSaveEditMember = async (e: FormEvent) => {
    e.preventDefault();
    if (!editingUserId) return;
    setEditFormError("");

    if (!editName.trim()) return setEditFormError("Name is required");
    if (!editEmail.trim() || !isValidEmail(editEmail.trim())) return setEditFormError("A valid email is required");
    if (editPassword.trim() && editPassword.length < 5) return setEditFormError("Password must be at least 5 characters");

    const branch = branchList.find((b) => b.id === editBranchId);
    const pos = positionsList.find((p) => p.id === editPositionId);

    setIsSaving(true);
    try {
      const payload: any = {
        name: editName.trim(),
        email: editEmail.trim(),
        phone: editPhone.trim() || null,
        branch: branch?.name || null,
        branchId: editBranchId || null,
        managerId: editManagerId || null,
        team: editTeamName.trim() || null,
      };

      if (pos) {
        payload.role = toRoleSlug(pos.name);
      }
      if (editPassword.trim()) {
        payload.password = editPassword.trim();
      }

      await axios.patch(
        `${API}/auth/company-user/update/${editingUserId}`,
        payload,
        { headers: authHeader() }
      );

      showToast("Team member profile updated successfully", "success");
      setEditModalOpen(false);
      setDetailPopup(null);
      await fetchCompanyUsers();
    } catch (err: any) {
      const message = err.response?.data?.message || "Failed to update profile";
      setEditFormError(message);
      showToast(message, "error");
    } finally {
      setIsSaving(false);
    }
  };

  // View leads handler
  const handleViewLeads = (member: TeamMember) => {
    navigate(`/leads?agent=${encodeURIComponent(member.name)}`);
  };

  // Open reassign modal
  const handleReassign = (member: TeamMember) => {
    setReassignMember(member);
    const otherAgent = teamMembers.find((m) => m.id !== member.id)?.name || "";
    setReassignTargetAgent(otherAgent);
    setReassignModalOpen(true);
  };

  // Confirm reassignment
  const handleConfirmReassign = async () => {
    if (!reassignMember || !reassignTargetAgent) return;
    setIsReassigning(true);
    try {
      // 1. Reassign all leads
      await axios.patch(
        `${API}/leads/reassign-agent`,
        {
          fromAgent: reassignMember.name,
          toAgent: reassignTargetAgent,
        },
        { headers: authHeader() }
      );

      // 2. Optionally transfer direct reports if requested
      if (reassignTransferReports) {
        const targetMemberObj = companyUsers.find((u) => u.name === reassignTargetAgent);
        if (targetMemberObj) {
          const directReports = companyUsers.filter((u) => u.managerId === reassignMember.id);
          for (const rep of directReports) {
            await axios.patch(
              `${API}/auth/company-user/update/${rep.id}`,
              { managerId: targetMemberObj.id },
              { headers: authHeader() }
            );
          }
        }
      }

      showToast(`Leads and assignments transferred to ${reassignTargetAgent} successfully`, "success");
      setReassignModalOpen(false);
      setDetailPopup(null);
      await fetchCompanyUsers();
    } catch (err: any) {
      showToast(err.response?.data?.message || "Failed to reassign", "error");
    } finally {
      setIsReassigning(false);
    }
  };

  const handlePositionChange = (posId: string) => {
    setNewPositionId(posId);
    setNewManagerId("");
  };

  useEffect(() => {
    if (autoSelectedManager) {
      setNewManagerId(autoSelectedManager.id);
    }
  }, [autoSelectedManager]);

  const getPosColor = (posId: string) => positionsList.find((p) => p.id === posId)?.color || "#64748b";
  const getPosName = (posId: string) => positionsList.find((p) => p.id === posId)?.name || "Unassigned";
  const getPosLevel = (posId: string) => positionsList.find((p) => p.id === posId)?.level || 0;

  const getDataAccessLabel = (posId: string) => {
    const pos = positionsList.find((p) => p.id === posId);
    if (!pos) return "Own Data Only";
    const map: Record<string, string> = {
      all: "All Company Data",
      region: "Department / Region Data",
      team: "Team Data Only",
      own_team: "Own + Team View",
      own: "Own Data Only",
      view: "View Only",
    };
    return map[pos.dataAccess] || "Own Data Only";
  };

  const levelBadgeClass = (posId: string) => {
    const color = getPosColor(posId);
    const map: Record<string, string> = {
      "#8b5cf6": "bg-purple-100 text-purple-700",
      "#4f46e5": "bg-indigo-100 text-indigo-700",
      "#3b82f6": "bg-blue-100 text-blue-700",
      "#14b8a6": "bg-teal-100 text-teal-700",
      "#22c55e": "bg-green-100 text-green-700",
      "#f59e0b": "bg-amber-100 text-amber-700",
    };
    return map[color] || "bg-gray-100 text-gray-600";
  };

  return (
    <CrmLayout title="Team Structure">
      <div className="p-5 md:p-6 space-y-5">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="space-y-1">
            <h1 className="text-2xl font-bold text-gray-900">Team Structure</h1>
            <p className="text-sm text-gray-500">
              Visualize your company hierarchy and manage team assignments
            </p>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => setViewMode("org")}
                className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all whitespace-nowrap cursor-pointer ${viewMode === "org"
                  ? "bg-indigo-600 text-white shadow-sm"
                  : "text-gray-600 hover:text-gray-900"
                  }`}
              >
                <i className="ri-git-branch-line mr-1 w-4 h-4 inline-flex items-center justify-center" />
                Org Chart
              </button>
              <button
                onClick={() => setViewMode("list")}
                className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all whitespace-nowrap cursor-pointer ${viewMode === "list"
                  ? "bg-indigo-600 text-white shadow-sm"
                  : "text-gray-600 hover:text-gray-900"
                  }`}
              >
                <i className="ri-list-check mr-1 w-4 h-4 inline-flex items-center justify-center" />
                List View
              </button>
            </div>
            {isSuperAdmin && (
              <button
                onClick={() => setAddModalOpen(true)}
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold transition-all duration-200 active:scale-[0.98] whitespace-nowrap cursor-pointer"
              >
                <i className="ri-add-line w-4 h-4 flex items-center justify-center" />
                Add Team Member
              </button>
            )}
          </div>
        </div>

        {/* Stats bar */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Total Members</p>
            <p className="text-2xl font-bold text-indigo-600 mt-0.5">{stats.totalMembers}</p>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Positions</p>
            <p className="text-2xl font-bold text-purple-600 mt-0.5">{stats.positions}</p>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Teams</p>
            <p className="text-2xl font-bold text-blue-600 mt-0.5">{stats.teams}</p>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Avg Leads/Agent</p>
            <p className="text-2xl font-bold text-green-600 mt-0.5">{stats.avgLeads}</p>
          </div>
        </div>

        {loadingUsers ? (
          <div className="py-16 flex items-center justify-center">
            <div className="w-8 h-8 border-2 border-indigo-200 border-t-indigo-600 rounded-full animate-spin" />
          </div>
        ) : viewMode === "org" ? (
          <OrgChart
            teamMembers={teamMembers}
            positions={positionsList}
            onEditMember={handleEditMember}
            onViewLeads={handleViewLeads}
            onReassign={handleReassign}
          />
        ) : (
          /* List View */
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 bg-gray-50">
                    <th className="text-left px-5 py-3 text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Employee</th>
                    <th className="text-left px-5 py-3 text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Position</th>
                    <th className="text-left px-5 py-3 text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Level</th>
                    <th className="text-left px-5 py-3 text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Reports To</th>
                    <th className="text-left px-5 py-3 text-[11px] font-semibold text-gray-500 uppercase tracking-wider hidden md:table-cell">Team</th>
                    <th className="text-left px-5 py-3 text-[11px] font-semibold text-gray-500 uppercase tracking-wider hidden lg:table-cell">Data Access</th>
                    <th className="text-left px-5 py-3 text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Leads</th>
                    <th className="text-right px-5 py-3 text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {teamMembers.map((m) => {
                    const bgClass = positionColors[getPosColor(m.positionId)] || "bg-gray-100 text-gray-700";
                    const pos = positionsList.find((p) => p.id === m.positionId);
                    const manager = m.reportsToMemberId
                      ? teamMembers.find((t) => t.id === m.reportsToMemberId)?.name || "—"
                      : "—";
                    return (
                      <tr
                        key={m.id}
                        className="border-b border-gray-100 hover:bg-gray-50 transition-colors"
                      >
                        <td className="px-5 py-3.5" onClick={() => setDetailPopup(m)}>
                          <div className="flex items-center gap-3 cursor-pointer">
                            <div className={`w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${bgClass}`}>
                              {m.avatarInitials}
                            </div>
                            <div className="min-w-0">
                              <p className="font-medium text-gray-900 truncate">{m.name}</p>
                              <p className="text-xs text-gray-500 truncate">{m.email}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-5 py-3.5 whitespace-nowrap">
                          <span className={`px-2.5 py-1 rounded-full text-[11px] font-semibold ${bgClass}`}>
                            {getPosName(m.positionId)}
                          </span>
                        </td>
                        <td className="px-5 py-3.5 whitespace-nowrap">
                          <span className={`inline-flex items-center justify-center w-7 h-7 rounded-full text-[11px] font-bold ${levelBadgeClass(m.positionId)}`}>
                            {getPosLevel(m.positionId)}
                          </span>
                        </td>
                        <td className="px-5 py-3.5 text-gray-600 whitespace-nowrap">{manager}</td>
                        <td className="px-5 py-3.5 text-gray-600 whitespace-nowrap hidden md:table-cell">{m.teamName}</td>
                        <td className="px-5 py-3.5 whitespace-nowrap hidden lg:table-cell">
                          <span className={`px-2.5 py-1 rounded-full text-[11px] font-semibold ${dataAccessBadgeColors[pos?.dataAccess || "own"]}`}>
                            {getDataAccessLabel(m.positionId)}
                          </span>
                        </td>
                        <td className="px-5 py-3.5 whitespace-nowrap">
                          <span className="px-2.5 py-1 rounded-full text-[11px] font-semibold bg-indigo-50 text-indigo-700">
                            {m.leadCount}
                          </span>
                        </td>
                        <td className="px-5 py-3.5 text-right whitespace-nowrap">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => handleEditMember(m)}
                              className="px-2.5 py-1 rounded-lg text-xs font-medium text-indigo-600 border border-indigo-200 hover:bg-indigo-50 transition-colors cursor-pointer"
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => handleViewLeads(m)}
                              className="px-2.5 py-1 rounded-lg text-xs font-medium text-gray-600 border border-gray-200 hover:bg-gray-50 transition-colors cursor-pointer"
                            >
                              Leads
                            </button>
                            <button
                              onClick={() => handleReassign(m)}
                              className="px-2.5 py-1 rounded-lg text-xs font-medium text-amber-600 border border-amber-200 hover:bg-amber-50 transition-colors cursor-pointer"
                            >
                              Reassign
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Detail popup in List View */}
        {detailPopup && (
          <>
            <div className="fixed inset-0 z-40 bg-black/20" onClick={() => setDetailPopup(null)} />
            <div className="fixed z-50 bg-white rounded-xl border border-gray-200 shadow-2xl p-5 w-80"
              style={{ top: "50%", left: "50%", transform: "translate(-50%, -50%)" }}
            >
              <div className="text-center">
                <div className={`w-14 h-14 rounded-full mx-auto mb-3 flex items-center justify-center text-lg font-bold ${positionColors[getPosColor(detailPopup.positionId)] || "bg-gray-100 text-gray-700"}`}>
                  {detailPopup.avatarInitials}
                </div>
                <h3 className="text-base font-semibold text-gray-900">{detailPopup.name}</h3>
                <span className={`inline-block mt-1 px-2.5 py-0.5 rounded-full text-[11px] font-semibold ${positionColors[getPosColor(detailPopup.positionId)] || "bg-gray-100 text-gray-700"}`}>
                  {getPosName(detailPopup.positionId)}
                </span>
                <div className="mt-3 space-y-1.5 text-sm text-gray-600 text-left bg-gray-50 p-3 rounded-lg">
                  <p className="flex items-center gap-2 truncate"><i className="ri-mail-line text-gray-400" />{detailPopup.email}</p>
                  <p className="flex items-center gap-2"><i className="ri-phone-line text-gray-400" />{detailPopup.phone}</p>
                  <p className="flex items-center gap-2"><i className="ri-building-line text-gray-400" />{typeof detailPopup.branch === "object" && detailPopup.branch !== null ? detailPopup.branch.name : detailPopup.branch || "—"}</p>
                </div>
                <div className="mt-4 flex flex-col gap-2">
                  <button
                    onClick={() => { handleEditMember(detailPopup); setDetailPopup(null); }}
                    className="w-full px-3 py-2 rounded-lg text-xs font-semibold text-white bg-indigo-600 hover:bg-indigo-700 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    Edit Profile
                  </button>
                  <button
                    onClick={() => { handleViewLeads(detailPopup); setDetailPopup(null); }}
                    className="w-full px-3 py-2 rounded-lg text-xs font-medium text-gray-700 border border-gray-200 hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    View Their Leads
                  </button>
                  <button
                    onClick={() => { handleReassign(detailPopup); setDetailPopup(null); }}
                    className="w-full px-3 py-2 rounded-lg text-xs font-medium text-amber-700 border border-amber-300 hover:bg-amber-50 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    Reassign
                  </button>
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Edit Team Member Modal */}
      {editModalOpen && (
        <>
          <div className="fixed inset-0 bg-black/40 z-50" onClick={() => !isSaving && setEditModalOpen(false)} />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl w-full max-w-md shadow-2xl animate-slide-in">
              <div className="flex items-center justify-between px-6 py-5 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-900">Edit Team Member</h2>
                <button
                  onClick={() => !isSaving && setEditModalOpen(false)}
                  className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors cursor-pointer"
                >
                  <i className="ri-close-line text-xl w-5 h-5 flex items-center justify-center" />
                </button>
              </div>

              <form onSubmit={handleSaveEditMember} className="px-6 py-5 space-y-4 max-h-[75vh] overflow-y-auto">
                {editFormError && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                    <p className="text-xs text-red-700">{editFormError}</p>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Full Name <span className="text-red-500">*</span></label>
                  <input
                    type="text"
                    required
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    className="w-full px-3.5 py-2.5 rounded-lg border border-gray-200 bg-white text-sm text-gray-900 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-200"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Email Address <span className="text-red-500">*</span></label>
                  <input
                    type="email"
                    required
                    value={editEmail}
                    onChange={(e) => setEditEmail(e.target.value)}
                    className="w-full px-3.5 py-2.5 rounded-lg border border-gray-200 bg-white text-sm text-gray-900 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-200"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Phone Number</label>
                  <input
                    type="tel"
                    value={editPhone}
                    onChange={(e) => setEditPhone(e.target.value)}
                    className="w-full px-3.5 py-2.5 rounded-lg border border-gray-200 bg-white text-sm text-gray-900 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-200"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Position / Role</label>
                  <select
                    value={editPositionId}
                    onChange={(e) => setEditPositionId(e.target.value)}
                    className="w-full px-3.5 py-2.5 rounded-lg border border-gray-200 bg-white text-sm text-gray-900 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-200 cursor-pointer"
                  >
                    <option value="">Select position...</option>
                    {positionsList.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name} (Level {p.level})
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Reports To (Direct Manager)</label>
                  <select
                    value={editManagerId}
                    onChange={(e) => setEditManagerId(e.target.value)}
                    className="w-full px-3.5 py-2.5 rounded-lg border border-gray-200 bg-white text-sm text-gray-900 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-200 cursor-pointer"
                  >
                    <option value="">No manager (Top level)</option>
                    {editEligibleManagers.map((m) => (
                      <option key={m.id} value={m.id}>
                        {m.name} ({m.role})
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Branch</label>
                  <select
                    value={editBranchId}
                    onChange={(e) => setEditBranchId(e.target.value)}
                    className="w-full px-3.5 py-2.5 rounded-lg border border-gray-200 bg-white text-sm text-gray-900 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-200 cursor-pointer"
                  >
                    <option value="">Select branch...</option>
                    {branchList.map((b) => (
                      <option key={b.id} value={b.id}>
                        {b.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Team Name</label>
                  <input
                    type="text"
                    value={editTeamName}
                    onChange={(e) => setEditTeamName(e.target.value)}
                    placeholder="e.g. Sales Team Alpha"
                    className="w-full px-3.5 py-2.5 rounded-lg border border-gray-200 bg-white text-sm text-gray-900 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-200"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    New Password <span className="text-xs text-gray-400 font-normal">(leave blank to keep unchanged)</span>
                  </label>
                  <input
                    type="password"
                    value={editPassword}
                    onChange={(e) => setEditPassword(e.target.value)}
                    placeholder="••••••••"
                    className="w-full px-3.5 py-2.5 rounded-lg border border-gray-200 bg-white text-sm text-gray-900 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-200"
                  />
                </div>

                <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={() => setEditModalOpen(false)}
                    className="px-4 py-2.5 rounded-lg border border-gray-200 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSaving}
                    className="px-5 py-2.5 rounded-lg text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50"
                  >
                    {isSaving ? "Saving..." : "Save Changes"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </>
      )}

      {/* Reassign Modal */}
      {reassignModalOpen && reassignMember && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-xl w-full max-w-md shadow-2xl animate-slide-in p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                <i className="ri-user-shared-line text-amber-600 text-lg" />
              </div>
              <div>
                <h3 className="text-base font-semibold text-gray-900">
                  Reassign from {reassignMember.name}
                </h3>
                <p className="text-xs text-gray-500">
                  Currently holding {reassignMember.leadCount} leads
                </p>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Transfer All Leads To: <span className="text-red-500">*</span>
                </label>
                <select
                  value={reassignTargetAgent}
                  onChange={(e) => setReassignTargetAgent(e.target.value)}
                  className="w-full px-3.5 py-2.5 rounded-lg border border-gray-200 bg-white text-sm text-gray-900 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-200 cursor-pointer"
                >
                  <option value="">Select target team member...</option>
                  {teamMembers
                    .filter((m) => m.id !== reassignMember.id)
                    .map((m) => (
                      <option key={m.id} value={m.name}>
                        {m.name} ({m.leadCount} current leads)
                      </option>
                    ))}
                </select>
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <label className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={reassignTransferReports}
                    onChange={(e) => setReassignTransferReports(e.target.checked)}
                    className="mt-0.5 rounded text-indigo-600 accent-indigo-600 cursor-pointer"
                  />
                  <span className="text-xs text-amber-800">
                    Also transfer all direct reports (team hierarchy) to the selected target member
                  </span>
                </label>
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 mt-6">
              <button
                onClick={() => setReassignModalOpen(false)}
                className="px-4 py-2.5 rounded-lg border border-gray-200 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmReassign}
                disabled={!reassignTargetAgent || isReassigning}
                className="px-5 py-2.5 rounded-lg bg-amber-600 hover:bg-amber-700 text-sm font-semibold text-white transition-colors disabled:opacity-50 whitespace-nowrap cursor-pointer"
              >
                {isReassigning ? "Transferring..." : "Confirm Reassignment"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Team Member Modal */}
      {addModalOpen && (
        <>
          <div className="fixed inset-0 bg-black/40 z-40" onClick={() => !isSaving && setAddModalOpen(false)} />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl w-full max-w-md shadow-lg">
              <div className="flex items-center justify-between px-6 py-5 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-900">Add Team Member</h2>
                <button
                  onClick={() => !isSaving && setAddModalOpen(false)}
                  className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors cursor-pointer"
                >
                  <i className="ri-close-line text-xl w-5 h-5 flex items-center justify-center" />
                </button>
              </div>

              <form onSubmit={handleAddMember} className="px-6 py-5 space-y-4 max-h-[70vh] overflow-y-auto">
                {formError && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                    <p className="text-xs text-red-700">{formError}</p>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Full Name <span className="text-red-500">*</span></label>
                  <input
                    type="text"
                    required
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                    placeholder="e.g. Rahul Sharma"
                    className="w-full px-3.5 py-2.5 rounded-lg border border-gray-200 bg-white text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-200"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Email Address <span className="text-red-500">*</span></label>
                  <input
                    type="email"
                    required
                    value={newEmail}
                    onChange={(e) => setNewEmail(e.target.value)}
                    placeholder="rahul@company.com"
                    className="w-full px-3.5 py-2.5 rounded-lg border border-gray-200 bg-white text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-200"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Phone Number</label>
                  <input
                    type="tel"
                    value={newPhone}
                    onChange={(e) => setNewPhone(e.target.value)}
                    placeholder="+91 98765 43210"
                    className="w-full px-3.5 py-2.5 rounded-lg border border-gray-200 bg-white text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-200"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Password <span className="text-red-500">*</span></label>
                  <input
                    type="password"
                    required
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="••••••••"
                    className="w-full px-3.5 py-2.5 rounded-lg border border-gray-200 bg-white text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-200"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Position / Role <span className="text-red-500">*</span></label>
                  <select
                    required
                    value={newPositionId}
                    onChange={(e) => handlePositionChange(e.target.value)}
                    className="w-full px-3.5 py-2.5 rounded-lg border border-gray-200 bg-white text-sm text-gray-900 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-200 cursor-pointer"
                  >
                    <option value="">Select a position...</option>
                    {positionsList.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name} (Level {p.level})
                      </option>
                    ))}
                  </select>
                </div>

                {selectedPosition && selectedPosition.level > 1 && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      Direct Manager <span className="text-red-500">*</span>
                    </label>
                    <select
                      required
                      value={effectiveManagerId}
                      onChange={(e) => setNewManagerId(e.target.value)}
                      className="w-full px-3.5 py-2.5 rounded-lg border border-gray-200 bg-white text-sm text-gray-900 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-200 cursor-pointer"
                    >
                      <option value="">Select manager...</option>
                      {eligibleManagers.map((m) => (
                        <option key={m.id} value={m.id}>
                          {m.name} ({m.role})
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Branch</label>
                  <select
                    value={newBranchId}
                    onChange={(e) => setNewBranchId(e.target.value)}
                    className="w-full px-3.5 py-2.5 rounded-lg border border-gray-200 bg-white text-sm text-gray-900 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-200 cursor-pointer"
                  >
                    <option value="">Select branch...</option>
                    {branchList.map((b) => (
                      <option key={b.id} value={b.id}>
                        {b.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={() => setAddModalOpen(false)}
                    className="px-4 py-2.5 rounded-lg border border-gray-200 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={!canSubmit || isSaving}
                    className={`px-4 py-2.5 rounded-lg text-sm font-medium text-white transition-colors whitespace-nowrap cursor-pointer ${canSubmit && !isSaving
                      ? "bg-indigo-600 hover:bg-indigo-700"
                      : "bg-gray-300 cursor-not-allowed"
                      }`}
                  >
                    {isSaving ? "Adding..." : "Add Member"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </>
      )}
    </CrmLayout>
  );
}